import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { ROUTES } from '@/routes';
import { Container } from '@/components/layout/Container';
import { useAuth } from '@/contexts/AuthContext';
import { BrandMark } from './BrandMark';
import { MobileNav } from './MobileNav';
import { ThemeToggle } from './ThemeToggle';
import { PRIMARY_NAV_LINKS } from './nav-links';

export function NavBar() {
  const { t } = useTranslation(['nav']);
  const { session, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <Container size="xl">
        <div className="flex h-16 items-center justify-between gap-4">
          <BrandMark />

          <nav
            aria-label={t('nav:primary.label')}
            className="hidden items-center gap-1 md:flex"
          >
            {PRIMARY_NAV_LINKS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive
                    ? 'relative rounded-md px-3 py-2 text-sm font-medium text-foreground after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:rounded-full after:bg-brand'
                    : 'rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors duration-fast ease-out-quart hover:bg-muted hover:text-foreground'
                }
              >
                {t(item.i18nKey, { ns: 'nav' })}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <ThemeToggle />
            {session ? (
              <>
                <Link
                  to={ROUTES.account}
                  className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  {t('nav:auth.account')}
                </Link>
                <button
                  type="button"
                  onClick={() => { void signOut(); }}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors duration-fast ease-out-quart hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('nav:auth.sign_out')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to={ROUTES.authSignIn}
                  className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  {t('nav:auth.sign_in')}
                </Link>
                <Link
                  to={ROUTES.authSignUp}
                  className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-brand-foreground shadow-soft transition-colors duration-fast ease-out-quart hover:bg-brand-400"
                >
                  {t('nav:auth.sign_up')}
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-1 md:hidden">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={t('nav:mobile.open_menu')}
              className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Container>

      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
